import { apiClient } from "~/services/apiClient";
import type { SearchResponse } from "~/types/search";
import { toSearchMeta } from "~/types/search";
import type { AgentPresence } from "~/services/calling/types";
import { toResolutionCategoryValue } from "~/utils/callResolution";
import { phonesMatch } from "~/utils/phone";
import {
  pickOutboundCallLogMatch,
  type OutboundCallLogCandidate,
} from "~/context/call/outboundCallLink";

export async function endCallLog(uniqueId: string, hangupCause?: string) {
  if (!uniqueId) return;
  await apiClient.post(`/api/calls/${uniqueId}/end`, {
    ...(hangupCause ? { hangupCause } : {}),
  });
}

/**
 * Saves the operator's resolution on the call, which is what the alarm report and the
 * communication history show (AC-408). The follow-up task is created separately beforehand.
 */
export async function resolveCallLog(
  uniqueId: string,
  resolution: { category: string; notes: string; followUpRequired: boolean }
) {
  await apiClient.post(`/api/calls/${uniqueId}/resolve`, {
    category: toResolutionCategoryValue(resolution.category),
    resolutionNotes: resolution.notes,
    followUpRequired: resolution.followUpRequired,
  });
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Sidecar owns OUTBOUND CallLog create/answer/end. When the operator dials from a
 * customer context, retry-find the sidecar row by dialed number and attach customerId.
 */
export async function linkRecentOutboundCallToCustomer(input: {
  dialedNumber: string;
  customerId: string;
  /** When the dial was placed, so an earlier call to the same number is never linked. */
  dialedAtMs?: number;
  attempts?: number;
  delayMs?: number;
}): Promise<string | null> {
  const dialedNumber = input.dialedNumber.trim();
  const customerId = input.customerId.trim();
  if (!dialedNumber || !customerId) return null;

  const dialedAtMs = input.dialedAtMs ?? Date.now();
  // The phone system writes the call log a moment after the dial, and an operator reaching an
  // emergency contact often waits through ringing, so the search is given about 15 seconds
  // rather than 9 before it gives up and the call stays unattached (AC-394).
  const attempts = Math.max(1, input.attempts ?? 20);
  const delayMs = Math.max(100, input.delayMs ?? 750);

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) {
      await sleep(delayMs);
    }

    const response = await apiClient.get<{ data?: OutboundCallLogCandidate[] }>(
      "/api/calls",
      {
        params: {
          callerNumber: dialedNumber,
          limit: 20,
          page: 1,
        },
      }
    );
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    const match = pickOutboundCallLogMatch(rows, {
      dialedNumber,
      customerId,
      dialedAtMs,
      numbersMatch: phonesMatch,
    });
    if (!match) continue;

    if (!match.alreadyLinked) {
      await updateCallCustomer(match.uniqueId, customerId);
    }
    return match.uniqueId;
  }

  return null;
}

export type ExportCallLogsCsvParams = {
  customerId?: string;
  callerNumber?: string;
  customerName?: string;
  search?: string;
};

function parseFilenameFromContentDisposition(value?: string) {
  if (!value) return null;

  const encodedMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim());
    } catch {
      // Fall through to plain filename parsing.
    }
  }

  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return null;
}

export async function exportCallLogsCsv(params: ExportCallLogsCsvParams = {}) {
  const response = await apiClient.get<Blob>("/api/calls/export", {
    params: {
      format: "csv",
      ...params,
    },
    responseType: "blob",
  });

  const contentDisposition = response.headers?.["content-disposition"];
  return {
    blob: response.data,
    fileName: parseFilenameFromContentDisposition(contentDisposition),
  };
}

export async function updateCallCustomer(
  uniqueId: string,
  customerId: string
): Promise<{
  callId: string;
  customerId: string | null;
  customerName: string;
  deviceUnitId: string | null;
}> {
  const response = await apiClient.patch<{
    data?: {
      callId?: string;
      customerId?: string | null;
      customerName?: string;
      deviceUnitId?: string | null;
    };
  }>(`/api/calls/${encodeURIComponent(uniqueId)}/customer`, { customerId });

  const payload = response.data?.data ?? {};
  return {
    callId: payload.callId ?? uniqueId,
    customerId: payload.customerId ?? customerId,
    customerName: payload.customerName ?? "",
    deviceUnitId: payload.deviceUnitId ?? null,
  };
}

export async function getAvailableAgents(
  uniqueId: string,
  params: { search?: string; page?: number; limit?: number } = {}
): Promise<SearchResponse<AgentPresence>> {
  const response = await apiClient.get<{
    data?: AgentPresence[];
    meta?: unknown;
  }>(`/api/calls/${uniqueId}/available-agents`, { params });
  const data = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    data,
    meta: toSearchMeta(response.data?.meta, {
      query: params.search ?? "",
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      resultCount: data.length,
      totalCount: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    }),
  };
}
